import React from 'react';
import { Link, useParams, Navigate, useNavigate } from 'react-router-dom';
import { getTemplateDetail } from '../data/templateDetails';
import { useSEO } from '../hooks/useSEO';
import { useAuthStore } from '../store/authStore';
import FooterSection from '../components/landing/FooterSection';

const TemplateDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const template = getTemplateDetail(slug ?? '');
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  useSEO(
    template
      ? {
          title: template.seoTitle,
          description: template.seoDescription,
          canonical: `/templates/${template.slug}`,
        }
      : {
          title: 'Template Not Found',
          description: 'This template page does not exist.',
        }
  );

  if (!template) {
    return <Navigate to="/templates" replace />;
  }

  const handleUseTemplate = () => {
    const templateIdMap: Record<string, string> = {
      'start-stop-continue': 'start_stop_continue',
      'mad-sad-glad': 'mad_sad_glad',
      '4ls': 'four_ls',
      sailboat: 'sailboat',
    };
    const templateId = templateIdMap[template.slug] ?? template.slug;
    if (isAuthenticated) {
      navigate('/app/dashboard', { state: { createBoard: true, template: templateId } });
    } else {
      navigate('/register', { state: { template: templateId } });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-orange-800 to-slate-900 text-white pt-16 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link
            to="/templates"
            className="inline-flex items-center gap-1 text-orange-300 hover:text-white text-sm mb-8 transition-colors"
          >
            ← All templates
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <span className="text-5xl">{template.icon}</span>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold leading-tight">{template.name}</h1>
              <p className="text-orange-300 mt-1">{template.tagline}</p>
            </div>
          </div>

          <p className="text-slate-300 text-lg max-w-2xl mb-8">{template.description}</p>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleUseTemplate}
              className="px-6 py-3 bg-orange-500 hover:bg-orange-400 text-white font-semibold rounded-xl transition-colors"
            >
              Use This Template Free →
            </button>
            <Link
              to="/ai-retrospective-summary"
              className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors border border-white/20"
            >
              Generate AI Report
            </Link>
          </div>
        </div>
      </section>

      {/* When to use */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-12 border-b border-gray-100">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">When to Use This Template</h2>
        <ul className="space-y-2">
          {template.bestFor.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-gray-700">
              <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-orange-500" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Columns */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-12 border-b border-gray-100">
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Template Columns</h2>
        <p className="text-gray-500 mb-8">
          Each column has a specific purpose. Here's what belongs in each one — with real example cards.
        </p>

        <div className={`grid gap-6 ${template.columns.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4'}`}>
          {template.columns.map((col, i) => (
            <div key={i} className="rounded-2xl border border-gray-200 overflow-hidden">
              {/* Column header */}
              <div
                className="px-4 py-3 font-semibold text-sm"
                style={{ backgroundColor: col.colorBg, color: col.color }}
              >
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full mr-2"
                  style={{ backgroundColor: col.color }}
                />
                {col.name}
              </div>

              {/* Description */}
              <div className="px-4 pt-3 pb-2">
                <p className="text-xs text-gray-500 italic mb-3">{col.description}</p>

                {/* Example cards */}
                <div className="space-y-2">
                  {col.exampleCards.map((card, j) => (
                    <div
                      key={j}
                      className="px-3 py-2 rounded-lg text-sm text-gray-700 border border-gray-100 bg-gray-50"
                    >
                      {card}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* AI Summary example */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-12 border-b border-gray-100">
        <h2 className="text-2xl font-bold text-slate-900 mb-2">AI-Generated Summary Example</h2>
        <p className="text-gray-500 mb-6">
          After your retro, Retrosive's AI reads every card and vote to produce a structured summary like this — in about 10 seconds.
        </p>

        <div className="rounded-2xl border border-orange-200 bg-orange-50 p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-lg">🤖</span>
            <span className="text-sm font-semibold text-orange-600">AI-Generated Report</span>
          </div>
          <p className="text-slate-700 leading-relaxed whitespace-pre-line text-sm">
            {template.aiSummary}
          </p>
        </div>

        <div className="mt-6 p-5 bg-gradient-to-br from-orange-500 to-orange-700 rounded-2xl text-center">
          <p className="text-white font-semibold text-lg mb-3">
            Get this report for your next {template.name} automatically
          </p>
          <Link
            to="/ai-retrospective-summary"
            className="inline-block px-6 py-2.5 bg-white text-orange-600 font-bold rounded-xl hover:bg-orange-50 transition-colors"
          >
            Generate AI Report →
          </Link>
        </div>
      </section>

      {/* Related blog post */}
      {template.relatedBlogSlug && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 py-12 border-b border-gray-100">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">See a Real Example</h2>
          <Link
            to={`/blog/${template.relatedBlogSlug}`}
            className="flex items-center gap-4 p-5 rounded-2xl border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all group"
          >
            <span className="text-3xl">{template.icon}</span>
            <div>
              <p className="font-semibold text-slate-900 group-hover:text-orange-600 transition-colors">
                {template.relatedBlogTitle}
              </p>
              <p className="text-sm text-gray-500 mt-0.5">
                Full example with real team cards, voting results, and AI summary →
              </p>
            </div>
          </Link>
        </section>
      )}

      {/* Final CTA */}
      <section className="bg-slate-50 border-t border-gray-200 py-16">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-3">
            Ready to run your {template.name}?
          </h2>
          <p className="text-gray-500 mb-6">
            Start for free — no credit card required. Your board is ready in seconds.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleUseTemplate}
              className="px-8 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors"
            >
              Use This Template Free
            </button>
            <Link
              to="/ai-retrospective-summary"
              className="px-8 py-3 border border-gray-300 hover:border-orange-400 text-gray-700 font-medium rounded-xl transition-colors"
            >
              Generate AI Report
            </Link>
          </div>
        </div>
      </section>

      <FooterSection />
    </div>
  );
};

export default TemplateDetailPage;
